import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Quote, Linkedin } from 'lucide-react';
import SEO from '../components/SEO';

export const Testimonials = () => {
  const { t, i18n } = useTranslation();

  const testimonials = [
    {
      id: 1,
      name: 'Lapo Martini',
      role: 'DT Operations Leader | Citizen Development Champion | IT SOX Compliance Leader',
      date: 'November 15, 2024',
      content: {
        en: "I had the pleasure to work with Victor for more than a year and his professionalism is outstanding. He learned to navigate a complex environment in few months and he started immediately delivering results which have been recognized by several people in the organization. Victor has not only developed applications among SharePoint, PowerApps, Power Automate & PowerBI but he has also supported more than 200 critical business processes built over the MS Power Platform caring about business continuity. Although his commitment and say/do ratio are the distinctive characteristics, he is also a fantastic teammate to share time with. He would be an asset to any team.",
        it: "Ho avuto il piacere di lavorare con Victor per più di un anno e la sua professionalità è eccezionale. Ha imparato a navigare in un ambiente complesso in pochi mesi e ha iniziato immediatamente a produrre risultati che sono stati riconosciuti da diverse persone nell'organizzazione. Victor non ha solo sviluppato applicazioni tra SharePoint, PowerApps, Power Automate e PowerBI, ma ha anche supportato più di 200 processi aziendali critici costruiti sulla piattaforma MS Power Platform, prestando attenzione alla continuità aziendale. Sebbene il suo impegno e il rapporto tra dire e fare siano le caratteristiche distintive, è anche un fantastico compagno di squadra con cui condividere il tempo. Sarebbe una risorsa preziosa per qualsiasi team."
      },
      image: '',
      linkedin: 'https://www.linkedin.com/in/lapo-martini/'
    },
    {
      id: 2,
      name: 'Luca Bauleo',
      role: 'IT Manager presso Pharma Line srl',
      date: 'May 30, 2023',
      content: {
        en: "Victor has been an extremely capable and professional colleague, a cornerstone of my IT team. His technical preparation as a developer of data, technology Microsoft and his Service HelpDesk role reveal an excellent level of competence, demonstrating extraordinary effectiveness in using a wide range of tools, including PowerShell, PowerApps, Python and SQL. Victor has been involved in migration and data automation projects with excellent results and has contributed to raising our process standards, refining existing Apps and creating new ones with great results. His motivation, contagious enthusiasm, positivity and optimal work rhythm are a source of inspiration and have represented a precious value for our organization. Faced with complex challenges, Victor has always found a solution. His commitment to continuous training and his thirst for knowledge are synonymous with innovation and growth. His passion and energy will be the source of his future success, and as Victor teaches: 'You'll see that everything will be fine'.",
        it: "Victor è stato un collega decisamente capace e professionale, una colonna portante del mio team IT. La sua preparazione tecnica come sviluppatore di dati, su tecnologia Microsoft e il suo ruolo da Senior HelpDesk, rivelano un ottimo livello di competenza, dimostrando un'efficacia straordinaria nell'utilizzo di una vasta gamma di strumenti, tra cui PowerShell, PowerApps, Python e SQL. Victor non ha limitato solo a gestire progetti di migrazione e automazione dei dati con ottimi risultati ma ha contribuito ad elevare gli standard dei nostri processi, perfezionando le App e creandone di nuove con grandi risultati. La sua motivazione, il contagioso entusiasmo, la sua positività e l'ottimo ritmo lavorativo, sono fonte d'ispirazione e hanno rappresentato un valore prezioso per la nostra organizzazione. Di fronte a sfide complesse, Victor ha sempre pronta una soluzione. Il suo impegno nella formazione continua e la sua sete di conoscenza sono sinonimo di innovazione e crescita. La sua passione ed energia saranno per il suo futuro fonte di successo e come Victor insegna: 'Vedrai che andrà tutto bene'."
      },
      image: '',
      linkedin: 'https://www.linkedin.com/in/luca-bauleo/'
    },
    {
      id: 3,
      name: 'Fulvio Manno',
      role: 'National Sales Manager | Pharma Line srl',
      date: 'May 29, 2023',
      content: {
        en: "Victor has shown great availability and punctuality in proposing services, processes and applications that objectively improve our work. Seriousness and empathy, combined with a non-problematic vision, complete his profile. Thank you Victor for your professionalism and for the priceless support you have given to the whole team.",
        it: "Victor ha mostrato grande disponibilità e puntualità nel proporre servizi, processi ed applicazioni, che migliorassero oggettivamente il nostro lavoro. Serietà ed empatia, unite ad una vision non problematica, completano il suo profilo. Grazie Victor per la tua professionalità e per il supporto impagabile che hai reso a tutto il team."
      },
      image: '',
      linkedin: 'https://www.linkedin.com/in/fulvio-manno/'
    }
  ];

  return (
    <div className="pt-24 pb-12 px-4 max-w-7xl mx-auto bg-white dark:bg-gray-900 min-h-screen">
      <SEO
        title={`${t('navigation.testimonials') || 'Testimonials'} – Victor Lazari`}
        description={t('testimonials.title') as string}
        canonical={"https://victorlazari.com/testimonials"}
        image={"https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/varienda%20minimalist%20logo.png"}
        type="website"
      />
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-4xl md:text-5xl font-semibold text-center mb-12 text-slate-900 dark:text-gray-100"
      >
        {t('testimonials.title')}
      </motion.h1>

      <div className="grid grid-cols-1 gap-8">
        {testimonials.map((testimonial, index) => (
          <motion.div
            key={testimonial.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative bg-white dark:bg-gray-800 rounded-xl p-8 border border-slate-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 group"
          >
            <Quote className="absolute top-8 right-8 w-12 h-12 text-slate-200 dark:text-gray-700" />
            
            <div className="flex items-start gap-6 mb-6">
              <img
                src={testimonial.image}
                alt={testimonial.name}
                className="w-20 h-20 rounded-full object-cover border-2 border-slate-200 dark:border-gray-700"
              />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-gray-100">{testimonial.name}</h3>
                  <a
                    href={testimonial.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-gray-100 transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
                <p className="text-sm text-slate-600 dark:text-gray-300 mb-1">{testimonial.role}</p>
                <p className="text-xs text-slate-500 dark:text-gray-400">{testimonial.date}</p>
              </div>
            </div>

            <blockquote className="text-slate-700 dark:text-gray-300 relative text-lg leading-relaxed">
              "{testimonial.content[i18n.language.startsWith('it') ? 'it' : 'en']}"
            </blockquote>
          </motion.div>
        ))}
      </div>
    </div>
  );
};